import { Card, CardContent } from '../../../components/ui/card';
import { CheckCircle2, Clock, Target, Truck, TrendingDown, TrendingUp, type LucideIcon } from 'lucide-react';
import type { PerformanceTotals } from '../../../lib/api';

interface PerformanceKpiTilesProps {
  current: PerformanceTotals | null;
  prior: PerformanceTotals | null;
  target: { on_time: number; otif: number };
  priorLabel: string;
  loading: boolean;
}

interface Tile {
  label: string;
  value: string;
  sub: string;
  delta: number | null;
  /** Points for percentages, percent change for counts. */
  deltaUnit: 'pts' | '%';
  icon: LucideIcon;
  iconClass: string;
  cardClass?: string;
  testId: string;
}

function pctDelta(now: number | null | undefined, before: number | null | undefined): number | null {
  if (now === null || now === undefined || before === null || before === undefined) return null;
  return Math.round((now - before) * 10) / 10;
}

/**
 * Delivery on time, OTIF, required-date and lines delivered for the selected
 * range, each against the previous period of the same length. Only the
 * on-time tile takes the red state, when it is under target.
 */
export default function PerformanceKpiTiles({ current, prior, target, priorLabel, loading }: PerformanceKpiTilesProps) {
  const pct = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${v.toFixed(1)}%`);
  const onTimeBelow = current?.on_time !== null && current?.on_time !== undefined && current.on_time < target.on_time;
  const linesDelta = current && prior && prior.lines > 0
    ? Math.round(((current.lines - prior.lines) / prior.lines) * 1000) / 10
    : null;

  const tiles: Tile[] = [
    {
      label: 'Delivery on time',
      value: pct(current?.on_time),
      sub: `Target ${target.on_time}%`,
      delta: pctDelta(current?.on_time, prior?.on_time),
      deltaUnit: 'pts',
      icon: Clock,
      iconClass: onTimeBelow ? 'text-red-500' : 'text-green-500',
      cardClass: onTimeBelow ? 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30' : undefined,
      testId: 'supplier-performance-kpi-on-time',
    },
    {
      label: 'OTIF (within window)',
      value: pct(current?.otif),
      sub: `Target ${target.otif}%`,
      delta: pctDelta(current?.otif, prior?.otif),
      deltaUnit: 'pts',
      icon: Target,
      iconClass: 'text-blue-500',
      testId: 'supplier-performance-kpi-otif',
    },
    {
      label: 'Met required date',
      value: pct(current?.required),
      sub: 'Delivered by the date the site needed it',
      delta: pctDelta(current?.required, prior?.required),
      deltaUnit: 'pts',
      icon: CheckCircle2,
      iconClass: 'text-green-500',
      testId: 'supplier-performance-kpi-required',
    },
    {
      label: 'Lines delivered',
      value: current ? current.lines.toLocaleString() : '—',
      sub: current ? `${current.suppliers.toLocaleString()} suppliers reporting` : '',
      delta: linesDelta,
      deltaUnit: '%',
      icon: Truck,
      iconClass: 'text-slate-500',
      testId: 'supplier-performance-kpi-lines',
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-4" data-testid="supplier-performance-kpis">
      {tiles.map((tile) => {
        const Icon = tile.icon;
        const up = tile.delta !== null && tile.delta > 0;
        const Arrow = up ? TrendingUp : TrendingDown;
        return (
          <Card key={tile.label} className={tile.cardClass} data-testid={tile.testId}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm text-muted-foreground">{tile.label}</p>
                  {loading && !current ? (
                    <div className="mt-1 h-8 w-20 animate-pulse rounded bg-muted" />
                  ) : (
                    <p className="text-2xl font-bold">{tile.value}</p>
                  )}
                  <p className="text-xs text-muted-foreground truncate">{tile.sub}</p>
                  {tile.delta !== null && tile.delta !== 0 && (
                    <p className={`mt-1 flex items-center gap-1 text-xs ${up ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                      <Arrow className="h-3 w-3" />
                      {up ? '+' : ''}{tile.delta}{tile.deltaUnit === 'pts' ? ' pts' : '%'} vs {priorLabel}
                    </p>
                  )}
                </div>
                <Icon className={`h-8 w-8 shrink-0 ${tile.iconClass}`} />
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
